import type { PureQueryOptions } from '@apollo/client';
import { gql } from '~/apollo/client-v3';
import { useQuery } from '@apollo/client';
import * as R from 'ramda';
import * as fragments from '~/apollo/fragments';
import type {
  OutcropProductionRouteQuery,
  OutcropProductionRouteQueryVariables,
} from '~/apollo/generated/v3/graphql';
import { BookmarkParentType } from '~/apollo/generated/v3/graphql';
import { NotFound } from '~/components/common/NotFound';
import { SpinnerPlaceholder } from '~/components/common/SpinnerPlaceholder';
import { SupportingObjectList } from '~/components/supportingObject/SupportingObjectList';
import { TaggedSupportingObjectList } from '~/components/supportingObject/TaggedSupportingObjectList';
import { useOutcropOutletContext } from '~/routes/outcrop/$outcropId';

const OUTCROP_PRODUCTION_ROUTE = gql`
  query OutcropProductionRoute($outcropId: Int!) {
    outcropList(id: $outcropId) {
      ...outcropParts
      production {
        ...productionParts
        pictures {
          ...pictureParts
          file {
            ...fileParts
            signedUrl
          }
        }
        files {
          ...fileParts
          signedUrl
        }
      }

      studies {
        ...studyParts

        dataHistory {
          ...dataHistoryParts
        }

        production {
          ...productionParts
          pictures {
            ...pictureParts
            file {
              ...fileParts
              signedUrl
            }
          }
          files {
            ...fileParts
            signedUrl
          }
        }
      }
    }
  }

  ${fragments.outcropParts}
  ${fragments.productionParts}
  ${fragments.pictureParts}
  ${fragments.fileParts}
  ${fragments.studyParts}
  ${fragments.dataHistoryParts}
`;

export default function OutcropProductionRoute() {
  const ctx = useOutcropOutletContext();
  const outcropId = ctx.outcrop.id;

  const { data, loading } = useQuery<
    OutcropProductionRouteQuery,
    OutcropProductionRouteQueryVariables
  >(OUTCROP_PRODUCTION_ROUTE, { variables: { outcropId } });

  const refetchQueries: PureQueryOptions[] = [
    { query: OUTCROP_PRODUCTION_ROUTE, variables: { outcropId } },
  ];

  const outcrop = data?.outcropList.find(oc => oc.id === outcropId);
  const items = outcrop?.production ?? [];
  const outcropStudies = R.sortBy(R.prop('name'), outcrop?.studies ?? []);

  if (loading) return <SpinnerPlaceholder />;
  if (!outcrop) return <NotFound />;

  return (
    <>
      <SupportingObjectList
        items={items}
        soParentType="outcrop"
        soParentId={outcropId}
        bookmarkParentType={BookmarkParentType.ReservoirModel}
        refetchQueries={refetchQueries}
      />

      <TaggedSupportingObjectList
        outcropId={outcropId}
        studies={outcropStudies}
        supportObjectKey="production"
        refetchQueries={refetchQueries}
      />
    </>
  );
}
